// src/achievements/art.js — 🎨 the designed badge set (v129.49).
//
// Matt approved the gallery on 4 Aug ("ALL LOOKS GOOD") — this is that set,
// shipped. One visual system: a flat toy-bright ROUNDEL (disc + darker rim of
// the same hue) with one bold white glyph, coloured from the TE brand Pantones
// in assets/brand-src (485 red, 158/1495 oranges, 354 green, 326/3115 teals,
// 254 purple) plus the app's ELC green. Competitive wins take a metal ring.
//
// RULES THAT MUST SURVIVE EDITS:
//   * The TE & ELC brand MARKS are never drawn here — they come from official
//     files (assets/). This module draws badges, not logos.
//   * Every consumer keeps the EMOJI FALLBACK: an unknown badge_key returns
//     null and the caller renders the old scallop/emoji badge. A new badge
//     added to BADGES without art here must degrade, never blank.
//   * No gradients, no external requests, geometry only — same reasoning as
//     the rest of the artwork decision (crisp at every size, tiny, offline).

var HUES = {
  'demo-champions': '#DA291C',
  'addon-masters': '#981D97',
  'customer-favourites': '#FFB81C',
  'svr-stars': '#5B7F95',
  'learning-leaders': '#00C1DE',
  'compliance-champions': '#009E3D',
  'data-capture-kings': '#00B2A9',
  'most-improved': '#00B140',
  'perfect-week': '#009E3D',
  'right-on-time': '#E87722',
  'right-on-time-90': '#E87722',
  'right-on-time-365': '#E87722',
  'module-passed': '#00C1DE',
  'fully-trained': '#00B2A9',
  'outstanding-review': '#FFB81C',
  'induction-star': '#DA291C',
  'customer-heart': '#00C1DE',
  'weekly-learner': '#00B2A9',   // v132.2 — weekly training (docs/training-plan.md)
  'study-hall': '#FFB81C',       // v132.2
  star: '#F5B301',
};

// Darken a hex hue for the rim (no color-mix — the app supports older WebKit).
function darken(hex, f) {
  var n = parseInt(hex.slice(1), 16);
  var r = Math.round(((n >> 16) & 255) * f), g = Math.round(((n >> 8) & 255) * f), b = Math.round((n & 255) * f);
  return 'rgb(' + r + ',' + g + ',' + b + ')';
}

// Glyphs, centred on a 100×100 canvas. Simple geometry only.
function glyph(key, hue) {
  var dk = darken(hue, 0.62);
  var G = {
    'demo-champions': '<rect x="30" y="46" width="40" height="28" rx="3"/><rect x="26" y="36" width="48" height="12" rx="3"/><rect x="46" y="36" height="38" width="8" opacity=".55"/><circle cx="42" cy="32" r="6" fill="none" stroke="#fff" stroke-width="5"/><circle cx="58" cy="32" r="6" fill="none" stroke="#fff" stroke-width="5"/>',
    'addon-masters': '<path d="M34 30 h12 v22 a4 4 0 0 0 8 0 V30 h12 v22 a16 16 0 0 1 -32 0 Z"/><rect x="34" y="30" width="12" height="8" fill="#F3D6EF"/><rect x="54" y="30" width="12" height="8" fill="#F3D6EF"/>',
    'customer-favourites': '<path d="M50 24 l7.8 16.6 17.5 2.2 -12.9 12.2 3.4 17.6 -15.8 -8.8 -15.8 8.8 3.4 -17.6 -12.9 -12.2 17.5 -2.2 Z"/>',
    'svr-stars': '<circle cx="45" cy="45" r="15" fill="none" stroke="#fff" stroke-width="7"/><rect x="55" y="55" width="20" height="9" rx="4.5" transform="rotate(45 55 55)"/>',
    'learning-leaders': '<polygon points="50,28 78,40 50,52 22,40"/><rect x="34" y="46" width="32" height="14" rx="3"/><rect x="72" y="40" width="5" height="20" rx="2.5"/><circle cx="74.5" cy="63" r="4"/>',
    'compliance-champions': '<path d="M30 52 l14 14 26 -30" fill="none" stroke="#fff" stroke-width="10" stroke-linecap="round" stroke-linejoin="round"/>',
    'data-capture-kings': '<rect x="26" y="34" width="48" height="34" rx="5"/><rect x="32" y="42" width="14" height="14" rx="3" fill="' + dk + '"/><rect x="50" y="44" width="18" height="4" rx="2" fill="' + dk + '"/><rect x="50" y="52" width="12" height="4" rx="2" fill="' + dk + '"/>',
    'most-improved': '<path d="M28 66 l14 -14 9 8 18 -20" fill="none" stroke="#fff" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/><polygon points="72,34 60,36 70,46"/>',
    'perfect-week': '<rect x="28" y="32" width="44" height="38" rx="5"/><rect x="28" y="32" width="44" height="10" rx="5" fill="' + dk + '"/><path d="M38 55 l7 7 16 -16" fill="none" stroke="' + dk + '" stroke-width="7" stroke-linecap="round" stroke-linejoin="round"/>',
    'right-on-time': '<circle cx="50" cy="50" r="22" fill="none" stroke="#fff" stroke-width="7"/><path d="M50 38 v13 l9 6" fill="none" stroke="#fff" stroke-width="6" stroke-linecap="round"/>',
    'module-passed': '<path d="M50 32 c-6 -5 -16 -5 -22 -2 v36 c6 -3 16 -3 22 2 c6 -5 16 -5 22 -2 V30 c-6 -3 -16 -3 -22 2 Z"/><rect x="48" y="30" width="4" height="38" fill="' + dk + '"/>',
    // v132.2 — weekly training. An open book with lines of text (Weekly
    // Learner) and a schoolhouse with a door and two windows (Study Hall).
    'weekly-learner': '<path d="M50 30 c-8 -6 -18 -6 -26 -3 v40 c8 -3 18 -3 26 3 c8 -6 18 -6 26 -3 V27 c-8 -3 -18 -3 -26 3 Z"/><path d="M32 40 h12 M32 48 h12 M32 56 h12 M56 40 h12 M56 48 h12 M56 56 h12" stroke="' + dk + '" stroke-width="3" stroke-linecap="round" fill="none"/>',
    'study-hall': '<path d="M22 44 L50 24 L78 44 Z"/><rect x="28" y="42" width="44" height="32" rx="3"/><rect x="44" y="56" width="12" height="18" rx="2" fill="' + dk + '"/><rect x="33" y="48" width="8" height="8" rx="1" fill="' + dk + '"/><rect x="59" y="48" width="8" height="8" rx="1" fill="' + dk + '"/>',
    'fully-trained': '<circle cx="50" cy="44" r="16"/><circle cx="50" cy="44" r="9" fill="' + dk + '"/><polygon points="42,56 38,76 50,68 62,76 58,56"/>',
    'outstanding-review': '<path d="M50 24 l6 18 18 6 -18 6 -6 18 -6 -18 -18 -6 18 -6 Z"/><circle cx="72" cy="30" r="4"/><circle cx="30" cy="68" r="3.5"/>',
    'induction-star': '<path d="M50 22 c10 8 12 22 8 34 h-16 c-4 -12 -2 -26 8 -34 Z"/><circle cx="50" cy="42" r="5" fill="' + dk + '"/><polygon points="42,56 32,66 42,64"/><polygon points="58,56 68,66 58,64"/><rect x="46" y="62" width="8" height="10" rx="4" fill="#FFB81C"/>',
    'customer-heart': '<path d="M50 70 C34 58 28 48 32 39 c3 -7 13 -8 18 -1 c5 -7 15 -6 18 1 c4 9 -2 19 -18 31 Z"/>',
    star: '<path d="M50 22 l7 15 16 2 -11.5 11 2.7 16 -14.2 -7.7 -14.2 7.7 2.7 -16 -11.5 -11 16 -2 Z"/><polygon points="40,62 34,80 44,73"/><polygon points="60,62 66,80 56,73"/>',
  };
  G['right-on-time-90'] = G['right-on-time'];
  G['right-on-time-365'] = G['right-on-time'];
  return G[key] || null;
}

var MEDALS = { gold: '#F5B301', silver: '#C9CFD6', bronze: '#C77B41' };

// The renderer. Returns an SVG string, or NULL when this key has no art —
// the caller's emoji badge is the contract for that case.
//   medal: 'gold' | 'silver' | 'bronze' | null  (the podium ring)
export function badgeArtSvg(badgeKey, size, medal) {
  var hue = HUES[badgeKey];
  var g = glyph(badgeKey, hue || '#009E3D');
  if (!hue || !g) return null;
  // The two long-service Right On Time tiers carry their metal PERMANENTLY —
  // the badge itself is the 90/365-day medal.
  if (badgeKey === 'right-on-time-90') medal = medal || 'silver';
  if (badgeKey === 'right-on-time-365') medal = medal || 'gold';
  var ring = medal && MEDALS[medal] ? MEDALS[medal] : null;
  var inner = ring ? 41 : 46;
  return '<svg width="' + (size || 64) + '" height="' + (size || 64) + '" viewBox="0 0 100 100" role="img" aria-label="badge">' +
    (ring ? '<circle cx="50" cy="50" r="47" fill="none" stroke="' + ring + '" stroke-width="6"/>' : '') +
    '<circle cx="50" cy="50" r="' + inner + '" fill="' + hue + '"/>' +
    '<circle cx="50" cy="50" r="' + inner + '" fill="none" stroke="' + darken(hue, 0.72) + '" stroke-width="4"/>' +
    '<g fill="#fff" stroke="none">' + g + '</g></svg>';
}

// Which podium ring does an award row get? detail.podium position when the
// award carries one; nothing otherwise.
export function medalFor(award) {
  try {
    if (!award || !award.detail || !Array.isArray(award.detail.podium)) return null;
    var key = award.store || String(award.staff_id || '');
    var i = award.detail.podium.findIndex(function (p) { return p && String(p.key) === String(key); });
    return i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : null;
  } catch (e) { return null; }
}
